import { FC, useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash/fp';

import { Input } from '@/components/shadcn/ui/input';
import { Currency } from '@/constants';
import { useBtcLikeMempoolFees } from '@/networks/btc-like/hooks';
import { cn } from '@/utils';
import Big from '@/utils/Bigjs';

interface UtxoSelectModalInfoFeeRateInputProps {
  className?: string;
  vbyteEstimate: number | undefined;
  onChangeVbyteEstimate: (vbyteEstimate: number | undefined) => void;
}

export const UtxoSelectModalInfoFeeRateInput: FC<UtxoSelectModalInfoFeeRateInputProps> = ({
  className,
  vbyteEstimate,
  onChangeVbyteEstimate,
}) => {
  const { data: mempoolFees } = useBtcLikeMempoolFees(Currency.BTC);
  const [feeRate, setFeeRate] = useState<string | undefined>(
    Big(vbyteEstimate ?? 0)
      .round(2, 3)
      .toString()
  );

  const debouncedSetVbyteEstimate = useCallback(debounce(350, onChangeVbyteEstimate), []);

  useEffect(() => {
    if (Number(feeRate) !== vbyteEstimate) {
      debouncedSetVbyteEstimate(Number(feeRate));
    }
  }, [feeRate]);

  const isCustomFeeRateRisky =
    Big(vbyteEstimate || 0)
      .round(2, 3)
      .lt(0) ||
    Big(vbyteEstimate || 0)
      .round(2, 3)
      .lte(Math.min((mempoolFees?.minimumFee ?? 0) * 1.2, mempoolFees?.economyFee ?? 0));

  return (
    <Input
      autoFocus
      value={feeRate}
      placeholder={'sat/vB'}
      className={cn(
        'h-auto w-[80%] rounded-lg border-0 bg-transparent leading-none',
        isCustomFeeRateRisky && 'text-red-500',
        className
      )}
      onBlur={() => {
        if (!Number(feeRate)) {
          setFeeRate(mempoolFees?.economyFee?.toFixed(2) ?? '0');
        }
      }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        const formattedValue = value
          .replace(/[,]/, '.') // replace \, with \.
          .replace(/[^0-9.]/g, '') // remove all characters except digits and \.
          .replace(/(?<=\..*?)\./g, '') // remove all \. but first \.
          .replace(/^\.$/, '0.') // Edge.case, prevents input parsing with errors when startsWith('.')
          .replace(/^00+/, '0.0'); // Edge.case, allows to enter 0.0 via 00

        setFeeRate(formattedValue ?? undefined);
      }}
    />
  );
};
